import {
  memo, useEffect, useMemo, useRef,
} from '../../lib/teact/teact';
import { getGlobal } from '../../global';

import type { ApiFormattedText, ApiMessage, ApiStory } from '../../api/types';
import type { ObserveFn } from '../../hooks/useIntersectionObserver';
import type { ThreadId } from '../../types';
import { ApiMessageEntityTypes } from '../../api/types';

import { extractMessageText, stripCustomEmoji } from '../../global/helpers';
import { normalizeStreamingMarkdown } from '../../util/streaming/streamingMarkdownRenderer';
import { logTranslationDebugEvent, shouldDiscardTranslationAsOriginal } from '../../util/translationDebug';
import trimText from '../../util/trimText';
import { insertTextEntity, renderTextWithEntities } from './helpers/renderTextWithEntities';

import useLastCallback from '../../hooks/useLastCallback';
import useSyncEffect from '../../hooks/useSyncEffect';
import useUniqueId from '../../hooks/useUniqueId';

import StreamingText from './StreamingText';
import TypingWrapper from './TypingWrapper';

interface OwnProps {
  messageOrStory: ApiMessage | ApiStory;
  threadId?: ThreadId;
  forcedText?: ApiFormattedText;
  isForAnimation?: boolean;
  emojiSize?: number;
  highlight?: string;
  asPreview?: boolean;
  truncateLength?: number;
  isProtected?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  withTranslucentThumbs?: boolean;
  shouldRenderAsHtml?: boolean;
  inChatList?: boolean;
  forcePlayback?: boolean;
  focusedQuote?: string;
  focusedQuoteOffset?: number;
  isInSelectMode?: boolean;
  canBeEmpty?: boolean;
  maxTimestamp?: number;
  shouldAnimateTyping?: boolean;
  isStreamingContent?: boolean;
}

const MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS = 3;

function MessageText({
  messageOrStory,
  forcedText,
  isForAnimation,
  emojiSize,
  highlight,
  asPreview,
  truncateLength,
  isProtected,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  withTranslucentThumbs,
  shouldRenderAsHtml,
  inChatList,
  forcePlayback,
  focusedQuote,
  focusedQuoteOffset,
  isInSelectMode,
  canBeEmpty,
  maxTimestamp,
  threadId,
  shouldAnimateTyping,
  isStreamingContent,
}: OwnProps) {
  const sharedCanvasRef = useRef<HTMLCanvasElement>();
  const sharedCanvasHqRef = useRef<HTMLCanvasElement>();

  const textCacheBusterRef = useRef(0);

  // When streaming (or just finished), use incremental markdown renderer to avoid layout jump
  const rawTextForStreaming = !forcedText ? extractMessageText(messageOrStory, inChatList)?.text : undefined;
  if (isStreamingContent && (rawTextForStreaming !== undefined || messageOrStory.streamingPreviewText)) {
    // R1: when the message carries v5 RunAI activity, AssistantInlineExecRows
    // owns the live status surface (Thinking/Working/burst rows). The
    // StreamingText preview row would otherwise render a duplicate
    // "● Running command" line stacked above the v5 component (the
    // standalone-white-bubble symptom). Drop previewText in that case so the
    // v5 anchor is the only status row.
    //
    // S2 (RCA: qa/evidence/webchat-pv-ref/rca/rca-S2-double-thinking-early-stream.md):
    // for any incoming assistant streaming message, the v5 component
    // (AssistantInlineExecRows) owns the live status row — State A renders
    // "Thinking", State B renders "Working for {fmt}", State C/D render
    // bursts and/or the work-handle. The StreamingText preview pill MUST
    // yield to v5 for the entire incoming-stream lifecycle so we never get
    // two "Thinking" labels stacked in the same bubble (the t<200ms RED).
    //
    // PV refs:
    //   - minicodex codex-rs/tui/src/status_indicator_widget.rs:42-105
    //     (single StatusIndicatorWidget instance, exclusive `update_header`).
    //   - minicodex codex-rs/tui/src/chatwidget/status_surfaces.rs:638-644
    //     ("Thinking" label gated by `is_task_running()` — one source).
    //   - chat-repo prior art: MessageList.tsx:1200
    //     `isAiAgentTyping={isAiAgentTyping && !hasActiveStream}`.
    //
    // Predicate: v5 owns the Thinking surface when (a) actionItems already
    // present (State B+, covered by `hasV5Activity` below) OR (b) incoming
    // assistant message is streaming with no actionItems yet (State A —
    // v5 unconditionally mounts a "Thinking" shimmer per
    // AssistantInlineExecRows.tsx:103-114). Either branch ⇒ drop the
    // preview pill so the bubble has exactly one status row.
    //
    // Spec: docs/specs/webchat-agent-message-display.md §4 single status row.
    const v5ActionItems = ('runAiActivity' in messageOrStory)
      ? (messageOrStory as { runAiActivity?: { actionItems?: unknown[] } })
        .runAiActivity?.actionItems
      : undefined;
    const hasV5Activity = Boolean(v5ActionItems);
    const willV5OwnThinking = !messageOrStory.isOutgoing
      && isStreamingContent
      && !(v5ActionItems && v5ActionItems.length > 0);
    const previewTextForStreaming = (hasV5Activity || willV5OwnThinking)
      ? undefined
      : messageOrStory.streamingPreviewText;
    return (
      <StreamingText
        text={rawTextForStreaming ?? ''}
        finalText={normalizeStreamingMarkdown(rawTextForStreaming ?? '')}
        isStreaming={isStreamingContent}
        previewText={previewTextForStreaming}
      />
    );
  }

  // If translation returned the same text as original (e.g. same language), discard it
  // so the original entities (bold, etc.) are preserved.
  const originalFormattedText = extractMessageText(messageOrStory, inChatList);
  const isTranslationSameAsOriginal = shouldDiscardTranslationAsOriginal({
    originalText: originalFormattedText?.text,
    translatedText: forcedText?.text,
  });
  const effectiveForcedText = isTranslationSameAsOriginal ? undefined : forcedText;

  useEffect(() => {
    if (!isTranslationSameAsOriginal) return;

    const global = getGlobal();
    logTranslationDebugEvent(
      Boolean(global.sharedState.settings.shouldCollectTranslationDebugLogs),
      'translation_same_as_original',
      {
        chatId: 'chatId' in messageOrStory ? messageOrStory.chatId : undefined,
        messageId: 'id' in messageOrStory ? messageOrStory.id : undefined,
        originalLength: originalFormattedText?.text?.length,
        translatedLength: forcedText?.text?.length,
      },
    );
  }, [
    isTranslationSameAsOriginal,
    messageOrStory,
    originalFormattedText?.text?.length,
    forcedText?.text?.length,
  ]);

  // Final translated text may contain malformed markdown markers from the
  // translation backend. Treat it as plain text unless entities are present,
  // otherwise the streaming markdown parser can lock the renderer.

  const formattedText = effectiveForcedText || originalFormattedText;
  const adaptedFormattedText = isForAnimation && formattedText ? stripCustomEmoji(formattedText) : formattedText;
  const { text, entities } = adaptedFormattedText || {};

  const entitiesWithFocusedQuote = useMemo(() => {
    if (!text || !focusedQuote) return entities;

    const offsetIndex = text.indexOf(focusedQuote, focusedQuoteOffset);
    const index = offsetIndex >= 0 ? offsetIndex : text.indexOf(focusedQuote); // Fallback to first occurrence
    const lendth = focusedQuote.length;
    if (index >= 0) {
      return insertTextEntity(entities || [], {
        offset: index,
        length: lendth,
        type: ApiMessageEntityTypes.QuoteFocus,
      });
    }

    return entities;
  }, [text, entities, focusedQuote, focusedQuoteOffset]);

  const containerId = useUniqueId();

  useSyncEffect(() => {
    textCacheBusterRef.current += 1;
  }, [text, entitiesWithFocusedQuote]);

  const withSharedCanvas = useMemo(() => {
    const hasSpoilers = entitiesWithFocusedQuote?.some((e) => e.type === ApiMessageEntityTypes.Spoiler);
    if (hasSpoilers) {
      return false;
    }

    const customEmojisCount = entitiesWithFocusedQuote
      ?.filter((e) => e.type === ApiMessageEntityTypes.CustomEmoji).length || 0;
    return customEmojisCount >= MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS;
  }, [entitiesWithFocusedQuote]) || 0;

  const renderText = useLastCallback((t: ApiFormattedText) => {
    return renderTextWithEntities({
      text: t.text,
      entities: t.entities,
      allowMarkdownTablePromotion: !asPreview && !shouldRenderAsHtml,
      highlight,
      emojiSize,
      shouldRenderAsHtml,
      containerId,
      asPreview,
      isProtected,
      observeIntersectionForLoading,
      observeIntersectionForPlaying,
      withTranslucentThumbs,
      sharedCanvasRef,
      sharedCanvasHqRef,
      cacheBuster: textCacheBusterRef.current.toString(),
      forcePlayback,
      isInSelectMode,
      maxTimestamp,
      chatId: 'chatId' in messageOrStory ? messageOrStory.chatId : undefined,
      messageId: messageOrStory.id,
      threadId,
    });
  });

  if (!text && !canBeEmpty) {
    return undefined;
  }

  const textToRender: ApiFormattedText = {
    text: trimText(text || '', truncateLength),
    entities: entitiesWithFocusedQuote,
  };

  return (
    <>
      {[
        withSharedCanvas && <canvas key="shared-canvas" ref={sharedCanvasRef} className="shared-canvas" />,
        withSharedCanvas && <canvas key="shared-canvas-hq" ref={sharedCanvasHqRef} className="shared-canvas" />,
        shouldAnimateTyping ? (
          <TypingWrapper key="typing-wrapper" text={textToRender}>{renderText}</TypingWrapper>
        ) : renderText(textToRender),
      ].flat().filter(Boolean)}
    </>
  );
}

export default memo(MessageText);
